import React, { useMemo, useState, useRef } from 'react';
import { View, Text, StyleSheet, PanResponder, LayoutChangeEvent } from 'react-native';
import Svg, { Path, Circle, Line, Defs, LinearGradient, Stop } from 'react-native-svg';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface AreaChartPoint {
  date: string;
  count: number;
}

export interface AreaChartCardProps {
  title?: string;
  data: AreaChartPoint[];
  height?: number;
  /** Sparkline mode: no axis labels, no tooltip, smaller — used for the
   * per-channel mini cards instead of a third chart component. */
  sparkline?: boolean;
}

/** Hand-rolled SVG area chart — no charting library in this codebase (none
 * installed, react-native-svg is the only drawing primitive available). Fill
 * path is built by hand (line across the top, closed down to the baseline),
 * stroke drawn on top per the usual "2px line over a low-opacity fill" mark
 * spec. Crosshair/tooltip on drag via PanResponder (works on both native
 * touch and RNW's forwarded pointer events). */
export const AreaChartCard: React.FC<AreaChartCardProps> = ({ title, data, height = 180, sparkline = false }) => {
  const styles = useThemedStyles(getStyles);
  const [width, setWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const widthRef = useRef(0);

  const onLayout = (e: LayoutChangeEvent) => {
    widthRef.current = e.nativeEvent.layout.width;
    setWidth(e.nativeEvent.layout.width);
  };

  const { linePath, fillPath, points, maxValue } = useMemo(() => {
    if (data.length === 0 || width === 0) return { linePath: '', fillPath: '', points: [] as { x: number; y: number }[], maxValue: 0 };
    const padding = sparkline ? 4 : 12;
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;
    const max = Math.max(...data.map((d) => d.count), 1);
    const step = data.length > 1 ? usableWidth / (data.length - 1) : 0;

    const pts = data.map((d, i) => ({
      x: padding + i * step,
      y: padding + usableHeight - (d.count / max) * usableHeight,
    }));

    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    const fill = `${line} L ${pts[pts.length - 1].x},${height - padding} L ${pts[0].x},${height - padding} Z`;

    return { linePath: line, fillPath: fill, points: pts, maxValue: max };
  }, [data, width, height, sparkline]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => !sparkline && points.length > 0,
      onMoveShouldSetPanResponder: () => !sparkline && points.length > 0,
      onPanResponderMove: (evt) => {
        if (points.length === 0 || widthRef.current === 0) return;
        // locationX is relative to the touched element itself (this View),
        // unlike moveX/pageX which are screen-absolute — exactly what's
        // needed to map the touch onto the chart's own point coordinates.
        const relativeX = Math.max(0, Math.min(widthRef.current, evt.nativeEvent.locationX));
        let nearest = 0;
        let nearestDist = Infinity;
        points.forEach((p, i) => {
          const dist = Math.abs(p.x - relativeX);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        });
        setActiveIndex(nearest);
      },
      onPanResponderRelease: () => setActiveIndex(null),
      onPanResponderTerminate: () => setActiveIndex(null),
    })
  ).current;

  const active = activeIndex != null ? points[activeIndex] : null;
  const activePoint = activeIndex != null ? data[activeIndex] : null;

  return (
    <View>
      {!!title && <Text style={styles.title}>{title}</Text>}
      <View style={{ height }} onLayout={onLayout} {...(sparkline ? {} : panResponder.panHandlers)}>
        {width > 0 && data.length > 0 ? (
          <Svg width={width} height={height}>
            <Defs>
              <LinearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={colors.brand.primary as string} stopOpacity={0.35} />
                <Stop offset="1" stopColor={colors.brand.primary as string} stopOpacity={0.02} />
              </LinearGradient>
            </Defs>
            <Path d={fillPath} fill="url(#areaFill)" />
            <Path d={linePath} fill="none" stroke={colors.brand.primary as string} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {active && (
              <>
                <Line x1={active.x} y1={0} x2={active.x} y2={height} stroke={colors.border as string} strokeWidth={1} strokeDasharray="4 4" />
                <Circle cx={active.x} cy={active.y} r={5} fill={colors.brand.primary as string} stroke={colors.background.surface as string} strokeWidth={2} />
              </>
            )}
          </Svg>
        ) : (
          !sparkline && <View style={styles.empty}><Text style={styles.emptyText}>No data yet</Text></View>
        )}
      </View>
      {!sparkline && (
        <Text style={styles.footer}>
          {activePoint ? `${activePoint.date}: ${activePoint.count}` : `${data.length} days · peak ${maxValue}`}
        </Text>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: designTokens.spacing.sm,
  },
  footer: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
    marginTop: designTokens.spacing.xs,
    textAlign: 'center',
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
